
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles, Box, Typography } from '@material-ui/core';
import { ScrollContainer } from 'react-indiana-drag-scroll';

import { LandcoverTable, SpeciesTypeFilter } from 'src/scenes/Project/components';
import { chartDefaultOptions, htmlLegendPlugin }
  from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { deepObjClone } from 'src/utils/util';
import { ChartWrapper } from 'src/components';


const useStyles = makeStyles(theme => ({
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  scrollbar: {
    '&::-webkit-scrollbar': {
      width: theme.spacing(1),
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '6px',
      backgroundColor: theme.palette.common.gray300,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: '#555',
    },
  },
}));

const LandcoverStats = ({ landcoverAssetSelecteds, landcoverStats }) => {

  const classes = useStyles();

  const [ landcoverData, setLandcoverData ] = useState(null);
  const [ landcoverChart, setLandcoverChart ] = useState(null);
  const [ landcoverTableData, setLandcoverTableData ] = useState(null);
  const [ landcoverDisplayFilter, setLandcoverDisplayFilter ] = useState('Detalle');

  useEffect(() => {
    if (landcoverStats) {
      setLandcoverData(landcoverStats);
      const chartOptions = deepObjClone(chartDefaultOptions);
      chartOptions.scales.x.stacked = true;
      chartOptions.scales.y = {
        stacked: true,
        ticks: { callback: value => typeof value === 'number' ? value.toLocaleString('es-CL') : value },
      };
      chartOptions.plugins.tooltip = {
        callbacks: {
          label: context => {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (typeof context.parsed.y === 'number') {
              label += context.parsed.y.toLocaleString('es-CL');
            } else {
              label += context.parsed.y;
            }
            return label;
          },
        },
      };
      chartOptions.plugins.htmlLegend = { containerId: 'landcover-chart' };
      const tableData = { labels: landcoverStats.labels, datasets: landcoverStats.datasets };
      setLandcoverTableData(tableData);
      const chartData = { datasets: landcoverStats.datasets, labels: landcoverStats.labels };
      const result = { id: 'landcover-chart', title: 'Uso de suelo', chart: chartData, options: chartOptions, chartJsType: 'bar' };
      setLandcoverChart(result);
    }
  }, [ landcoverStats ]);

  useEffect(() => {
    if (landcoverData) {
      let selectedOption = 'datasets';
      if (landcoverDisplayFilter === 'Resumen') {
        selectedOption = 'groupedDatasets';
      }
      const tableData = { labels: landcoverData.labels, datasets: landcoverData[selectedOption] };
      setLandcoverTableData(tableData);
      const chartData = { datasets: landcoverData[selectedOption], labels: landcoverData.labels };
      const result = { ...landcoverChart, chart: chartData };
      setLandcoverChart(result);
    }
  //eslint-disable-next-line
  }, [ landcoverDisplayFilter ]);

  const hasLandcoverData = Boolean(landcoverData?.datasets.length > 0);
  const leftYear = landcoverAssetSelecteds?.leftAsset?.year;
  const rightYear = landcoverAssetSelecteds?.rightAsset?.year;

  return (
    <>
      {hasLandcoverData &&
        <Grid item xs={12}>
          <Grid container item xs={12} direction="column">
            <Grid container item spacing={1}>
              <Grid item xs={6}>
                <Box my={0.5} display="flex" alignItems="center" height={32}>
                  <Typography className={classes.subtitle} variant="body2" component="span">
                    Evolución por año
                  </Typography>
                </Box>
                <Box my={0.5} />
                <Box className={classes.group}>
                  {hasLandcoverData &&
                    <Box display={landcoverChart.chart.datasets.length === 1 ? 'flex' : 'block' } height='100%' alignItems='flex-end'>
                      <ScrollContainer hideScrollbars={false} className={classes.scrollbar}>
                        <Box id={landcoverChart.id} maxHeight={104} />
                      </ScrollContainer>
                      <ChartWrapper type={landcoverChart.chartJsType} options={landcoverChart.options}
                        data={landcoverChart.chart} plugins={[ htmlLegendPlugin ]} />
                    </Box>
                  }
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box my={0.5} display="flex" alignItems="center">
                  <Typography className={classes.subtitle} variant="body2" component="span">
                    Comparación de
                    <Typography variant="body2" display="inline" color="primary" component="span">
                      <strong> {landcoverAssetSelecteds?.leftAsset?.year} </strong>
                    </Typography>
                    contra
                    <Typography variant="body2" display="inline" color="primary" component="span">
                      <strong> {landcoverAssetSelecteds?.rightAsset?.year} </strong>
                    </Typography>
                  </Typography>
                  <Box flexGrow={1} />
                  <Box display="flex" className={classes.group} p={0.5} ml={1}>
                    <SpeciesTypeFilter speciesType={landcoverDisplayFilter}
                      setSpeciesType={setLandcoverDisplayFilter}
                      labels={{ left: 'Detalle', right: 'Resumen' }} showAllOption={false}
                    />
                  </Box>
                </Box>
                <Box my={0.5} />
                {hasLandcoverData &&
                  <LandcoverTable
                    data={landcoverTableData}
                    year1={parseInt(leftYear, 10)}
                    year2={parseInt(rightYear, 10)}
                  />
                }
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      }
    </>
  );
};

LandcoverStats.propTypes = {
  landcoverAssetSelecteds: PropTypes.shape({
    leftAsset: PropTypes.shape({ year: PropTypes.number }),
    rightAsset: PropTypes.shape({ year: PropTypes.number }),
  }),
  landcoverStats: PropTypes.object,
};


export { LandcoverStats };
